import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { AdminModerationService } from '@/shared/api'
import type { ModerationActionRequest, ModerationListRequest } from '@/shared/api'

// Ключи для React Query
const QUERY_KEYS = {
  pendingCouriers: (params?: ModerationListRequest) =>
    ['couriers', 'pending', params],
}

export function usePendingCouriers(params?: ModerationListRequest) {
  return useQuery({
    queryKey: QUERY_KEYS.pendingCouriers(params),
    queryFn: () => AdminModerationService.getPendingCouriers(params),
    staleTime: 2 * 60 * 1000, // 2 минуты (данные модерации более динамичные)
  })
}

export function useApproveCourier() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, request }: { userId: number; request: ModerationActionRequest }) =>
      AdminModerationService.approveCourier(userId, request),
    onSuccess: (response) => {
      toast.success(response.message || 'Курьер одобрен')
      
      // Инвалидируем кеш модерации
      queryClient.invalidateQueries({ queryKey: ['couriers', 'pending'] })
      // И общий список пользователей
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
    onError: (error: any) => {
      if (error?.status === 409) {
        queryClient.invalidateQueries({ queryKey: ['couriers', 'pending'] })
        return
      }
      if (!error?.alreadyToasted) {
        toast.error(error.detail || 'Ошибка одобрения курьера')
      }
    },
  })
}

export function useRejectCourier() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, request }: { userId: number; request: ModerationActionRequest }) =>
      AdminModerationService.rejectCourier(userId, request),
    onSuccess: (response) => {
      toast.success(response.message || 'Курьер отклонен')
      
      // Инвалидируем кеш модерации
      queryClient.invalidateQueries({ queryKey: ['couriers', 'pending'] })
      // И общий список пользователей
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
    onError: (error: any) => {
      if (error?.status === 409) {
        queryClient.invalidateQueries({ queryKey: ['couriers', 'pending'] })
        return
      }
      if (!error?.alreadyToasted) {
        toast.error(error.detail || 'Ошибка отклонения курьера')
      }
    },
  })
}

export function useReopenCourier() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ userId, request }: {
      userId: number
      request: { expected_status_moderation?: number }
    }) => AdminModerationService.reopenCourier(userId, request),
    onSuccess: (response) => {
      toast.success(response.message || 'Заявка возвращена на модерацию')
      queryClient.invalidateQueries({ queryKey: ['couriers', 'pending'] })
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
    onError: (error: any) => {
      if (error?.status === 409) {
        queryClient.invalidateQueries({ queryKey: ['couriers', 'pending'] })
        return
      }
      if (!error?.alreadyToasted) toast.error(error.detail || 'Ошибка пересмотра заявки')
    },
  })
}

export function useModerationHistory(userId: number, enabled: boolean) {
  return useQuery({
    queryKey: ['moderation', 'history', userId],
    queryFn: () => AdminModerationService.getModerationHistory(userId),
    enabled: enabled && userId > 0,
    staleTime: 30 * 1000,
  })
}
